// src/components/singalong/makeFadedSong.js
//
// Makes an MP3 of a topic's song that fades out exactly where its sing-along
// does: full volume up to the "End sing-along here" marker, then down to
// silence over FADE_SECONDS, and the file ends there. Put on the topic's Song
// field, the Music button and the sing-along video then finish together.
//
// HOW
// ---
// Not in real time. The song is decoded and re-rendered offline with the
// Web Audio API (a 3-minute song takes a few seconds), then encoded to MP3
// in the browser with @breezystack/lamejs — a maintained fork of lamejs whose
// ES-module build works under Create React App (the original package fails
// there with "MPEGMode is not defined"). It is loaded only when someone
// presses the button, so it adds nothing to the portal's normal bundle.
//
//   npm install @breezystack/lamejs
//
// Same CORS requirement as the video export: the song is downloaded from
// Storage, so the bucket's CORS setting must list the site you're on.

import { FADE_SECONDS } from "../../services/lyricSyncService";

const SAMPLE_RATE = 44100;
const KBPS = 192;
const FRAME = 1152; // samples per MP3 frame

export class FadedSongError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

const yieldToUi = () => new Promise((r) => setTimeout(r, 0));

const decode = (ctx, buffer) =>
  new Promise((resolve, reject) => {
    // Callback form as well as the promise, for older Safari.
    const p = ctx.decodeAudioData(buffer, resolve, reject);
    if (p && typeof p.then === "function") p.then(resolve, reject);
  });

const toInt16 = (f32) => {
  const out = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i += 1) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

export const canMakeFadedSong = () =>
  typeof window !== "undefined" &&
  typeof (window.OfflineAudioContext || window.webkitOfflineAudioContext) === "function";

/**
 * @returns {Promise<{ blob, seconds, endAt, stopAt }>}
 *
 * opts: { musicUrl, endAt, onProgress(fraction), signal }
 */
export const makeFadedSong = async ({ musicUrl, endAt, onProgress, signal }) => {
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!Offline) {
    throw new FadedSongError("unsupported", "This browser can't make audio files. Use the latest Chrome or Edge.");
  }
  if (!(Number(endAt) > 0)) {
    throw new FadedSongError("noend", "Set “End sing-along here” first — the faded song fades out from that point.");
  }
  const checkAbort = () => {
    if (signal?.aborted) throw new FadedSongError("aborted", "Stopped.");
  };
  const progress = (f) => onProgress && onProgress(Math.max(0, Math.min(1, f)));

  // 1. Download.
  let bytes;
  try {
    const res = await fetch(musicUrl, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    bytes = await res.arrayBuffer();
  } catch {
    throw new FadedSongError(
      "cors",
      "The song could not be downloaded. The storage bucket needs a one-time CORS setting."
    );
  }
  checkAbort();
  progress(0.05);

  // 2. Decode at 44.1 kHz (the MP3 standard rate).
  let song;
  try {
    song = await decode(new Offline(2, 1, SAMPLE_RATE), bytes);
  } catch {
    throw new FadedSongError("audio", "The song file could not be read.");
  }
  checkAbort();
  progress(0.15);

  if (endAt >= song.duration - 0.2) {
    throw new FadedSongError(
      "noend",
      "The end marker is at the very end of the song, so there is nothing to fade. Move it earlier."
    );
  }

  // 3. Render: full volume to endAt, linear fade to silence, stop.
  const stopAt = Math.min(endAt + FADE_SECONDS, song.duration);
  const channels = Math.min(2, song.numberOfChannels) || 1;
  const length = Math.ceil(stopAt * SAMPLE_RATE);
  const offline = new Offline(channels, length, SAMPLE_RATE);
  const src = offline.createBufferSource();
  src.buffer = song;
  const gain = offline.createGain();
  gain.gain.setValueAtTime(1, 0);
  gain.gain.setValueAtTime(1, endAt);
  gain.gain.linearRampToValueAtTime(0, stopAt);
  src.connect(gain);
  gain.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  checkAbort();
  progress(0.3);

  // 4. Encode to MP3 (loaded on demand).
  let Mp3Encoder;
  try {
    ({ Mp3Encoder } = await import("@breezystack/lamejs"));
  } catch {
    throw new FadedSongError(
      "encoder",
      "The MP3 encoder isn't installed. Run: npm install @breezystack/lamejs — then restart the app."
    );
  }
  const left = toInt16(rendered.getChannelData(0));
  const right = channels > 1 ? toInt16(rendered.getChannelData(1)) : null;
  const encoder = new Mp3Encoder(channels, SAMPLE_RATE, KBPS);
  const parts = [];
  const total = left.length;
  for (let i = 0, n = 0; i < total; i += FRAME, n += 1) {
    const l = left.subarray(i, i + FRAME);
    const out = right ? encoder.encodeBuffer(l, right.subarray(i, i + FRAME)) : encoder.encodeBuffer(l);
    if (out.length) parts.push(new Uint8Array(out));
    // Let the page breathe (and the progress bar move) every ~3 seconds of audio.
    if (n % 120 === 0) {
      progress(0.3 + 0.68 * (i / total));
      await yieldToUi();
      checkAbort();
    }
  }
  const tail = encoder.flush();
  if (tail.length) parts.push(new Uint8Array(tail));
  progress(1);

  return {
    blob: new Blob(parts, { type: "audio/mpeg" }),
    seconds: stopAt,
    endAt,
    stopAt,
  };
};

export default makeFadedSong;
